import * as m from '@staiter/i18n';
import { RunDetailTabsMap, type TaskType } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Breadcrumbs, EmptyState, PageHeader, Table } from '~/components/data';
import { ConflictCard, EntityAuditPanel } from '~/components/domain';
import { Button, ConfirmDialog } from '~/components/primitives';
import { ProvenanceChip, RuntimeStatusPill, StatusBadge } from '~/components/status';
import { approveRun, fetchActivityLog, fetchRun, rejectRun } from '~/lib/api/runs';
import { validateAuditHighlight } from '~/lib/audit/highlight-search';
import { TASK_TYPE_LABELS, labelOf } from '~/lib/i18n/labels';
import { useRunStream } from '~/lib/sse/useRunStream';
import shared from './_auth._shared.module.css';
import styles from './_auth.runs.$runId.module.css';

export const Route = createFileRoute('/_auth/runs/$runId')({
  // F105 (С-I): `?highlight=<audit_event_id>` — запись журнала, которую открыть в панели истории.
  validateSearch: validateAuditHighlight,
  component: RunDetail,
});

function RunDetail(): JSX.Element {
  const { runId } = useParams({ from: '/_auth/runs/$runId' });
  const { highlight: auditHighlight } = Route.useSearch();
  const qc = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: ['runs', runId],
    queryFn: () => fetchRun(runId),
  });

  const stream = useRunStream(runId, {
    onEvent: () => {
      void qc.invalidateQueries({ queryKey: ['runs', runId] });
    },
  });

  const tabs = useMemo<string[]>(() => {
    if (!data) return [];
    return [...(RunDetailTabsMap[data.task_type as TaskType] ?? [])] as string[];
  }, [data]);

  const [activeTab, setActiveTab] = useState<string>('source_conflicts');
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const runtimeStatus = stream.runtime_status ?? data?.runtime_status;
  const businessStatus = stream.business_status ?? data?.business_status;

  const approveMut = useMutation({
    mutationFn: async (approver: string) => approveRun(runId, approver, crypto.randomUUID()),
    onSuccess: () => {
      setApproveOpen(false);
      void refetch();
    },
  });
  const rejectMut = useMutation({
    mutationFn: async (reason: string) => rejectRun(runId, reason, crypto.randomUUID()),
    onSuccess: () => {
      setRejectOpen(false);
      void refetch();
    },
  });

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_runs(), to: '/runs' },
              { label: data ? data.id.slice(0, 8) : '…' },
            ]}
          />
        }
        title={
          data
            ? m.route_runs_detail_title_with_id({ id: data.id.slice(0, 8) })
            : m.route_runs_detail_title_fallback()
        }
        description={
          data
            ? m.route_runs_detail_description({
                task: labelOf(TASK_TYPE_LABELS, data.task_type as keyof typeof TASK_TYPE_LABELS),
                date_from: data.period.date_from,
                date_to: data.period.date_to,
              })
            : undefined
        }
        actions={
          <>
            {runtimeStatus && <RuntimeStatusPill runtime_status={runtimeStatus} />}
            {businessStatus && <StatusBadge status={businessStatus} />}
            {runtimeStatus === 'waiting_approval' && (
              <>
                <Button variant="primary" onClick={() => setApproveOpen(true)}>
                  {m.route_runs_detail_btn_approve()}
                </Button>
                <Button variant="destructive" onClick={() => setRejectOpen(true)}>
                  {m.route_runs_detail_btn_reject()}
                </Button>
              </>
            )}
          </>
        }
      />

      {runtimeStatus === 'running' && (
        <div
          role="progressbar"
          aria-valuenow={stream.progress}
          aria-valuemin={0}
          aria-valuemax={100}
          className={styles.progress}
        >
          <div
            className={styles.progressFill}
            // staiter-inline-style-ok: SSE progress % — runtime-computed
            style={{ width: `${stream.progress}%` }}
          />
        </div>
      )}

      <nav className={shared.tabs} role="tablist">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={activeTab === t}
            className={[shared.tab, activeTab === t ? shared.tabActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setActiveTab(t)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </nav>

      <section>
        {activeTab === 'source_conflicts' &&
          (data?.source_conflicts.length ? (
            data.source_conflicts.map((c) => (
              <ConflictCard
                key={c.id}
                conflict={c as unknown as Parameters<typeof ConflictCard>[0]['conflict']}
              />
            ))
          ) : (
            <EmptyState title={m.route_runs_detail_empty_conflicts()} />
          ))}
        {activeTab === 'anomalies' &&
          (data?.anomalies.length ? (
            <ul>
              {data.anomalies.map((a) => (
                <li key={a.id}>{a.subject}</li>
              ))}
            </ul>
          ) : (
            <EmptyState title={m.route_runs_detail_empty_anomalies()} />
          ))}
        {activeTab === 'recommended_actions' &&
          (data?.recommended_actions.length ? (
            <ul>
              {data.recommended_actions.map((a) => (
                <li key={a.id}>
                  {a.title}{' '}
                  <span className={shared.subtle}>
                    {m.route_runs_detail_tier_suffix({ tier: a.tier })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={m.route_runs_detail_empty_recommendations()} />
          ))}
        {activeTab === 'evidence' &&
          (data?.evidence.length ? (
            <div className={styles.evidenceList}>
              {data.evidence.map((e) => (
                <ProvenanceChip
                  key={e.id}
                  source={e.source}
                  {...(e.document_id ? { document_id: e.document_id } : {})}
                  {...(e.fetched_at ? { fetched_at: e.fetched_at } : {})}
                />
              ))}
            </div>
          ) : (
            <EmptyState title={m.route_runs_detail_empty_evidence()} />
          ))}
        {activeTab === 'observability' && (
          <pre className={styles.jsonPre}>{JSON.stringify(data?.observability ?? {}, null, 2)}</pre>
        )}
        {activeTab === 'activity_log' && <ActivityLogTab runId={runId} />}
        {activeTab === 'inventory_result' && (
          <pre className={styles.jsonPre}>
            {JSON.stringify(data?.inventory_result ?? null, null, 2)}
          </pre>
        )}
        {activeTab === 'writeoff_result' && (
          <pre className={styles.jsonPre}>
            {JSON.stringify(data?.writeoff_result ?? null, null, 2)}
          </pre>
        )}
        {activeTab === 'cost_result' && (
          <pre className={styles.jsonPre}>{JSON.stringify(data?.cost_result ?? null, null, 2)}</pre>
        )}
      </section>
      <EntityAuditPanel entity_type="run" entity_id={runId} highlight={auditHighlight} />

      <ConfirmDialog
        open={approveOpen}
        onCancel={() => setApproveOpen(false)}
        onConfirm={(v) => approveMut.mutate(v ?? '')}
        title={m.route_runs_detail_approve_title()}
        description={m.route_runs_detail_approve_description()}
        confirmLabel={m.route_runs_detail_btn_approve()}
        withInput
        inputLabel={m.route_runs_detail_approve_input_label()}
        inputPlaceholder={m.route_runs_detail_approve_input_placeholder()}
        loading={approveMut.isPending}
      />
      <ConfirmDialog
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onConfirm={(v) => rejectMut.mutate(v ?? '')}
        title={m.route_runs_detail_reject_title()}
        description={m.route_runs_detail_reject_description()}
        confirmLabel={m.route_runs_detail_btn_reject()}
        variant="danger"
        withInput
        inputLabel={m.route_runs_detail_reject_input_label()}
        loading={rejectMut.isPending}
      />
    </div>
  );
}

function tabLabel(tab: string): string {
  switch (tab) {
    case 'source_conflicts':
      return m.route_runs_detail_tab_source_conflicts();
    case 'anomalies':
      return m.route_runs_detail_tab_anomalies();
    case 'recommended_actions':
      return m.route_runs_detail_tab_recommended_actions();
    case 'evidence':
      return m.route_runs_detail_tab_evidence();
    case 'observability':
      return m.route_runs_detail_tab_observability();
    case 'activity_log':
      return m.route_runs_detail_tab_activity_log();
    case 'inventory_result':
      return m.route_runs_detail_tab_inventory_result();
    case 'writeoff_result':
      return m.route_runs_detail_tab_writeoff_result();
    case 'cost_result':
      return m.route_runs_detail_tab_cost_result();
    default:
      return tab;
  }
}

function ActivityLogTab({ runId }: { runId: string }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['runs', runId, 'activity'],
    queryFn: () => fetchActivityLog(runId),
    refetchInterval: 5_000,
  });
  return (
    <Table
      ariaLabel={m.route_runs_detail_activity_aria()}
      loading={isLoading}
      data={data?.items ?? []}
      rowKey={(r) => r.id}
      columns={[
        {
          id: 'at',
          header: m.route_runs_detail_col_time(),
          accessor: (r) => r.occurred_at.slice(0, 19).replace('T', ' '),
          mono: true,
        },
        {
          id: 'type',
          header: m.route_runs_detail_col_event(),
          accessor: (r) => r.event_type,
          mono: true,
        },
        {
          id: 'payload',
          header: m.route_runs_detail_col_payload(),
          accessor: (r) => JSON.stringify(r.payload),
        },
        {
          id: 'trace',
          header: m.route_runs_detail_col_trace(),
          accessor: (r) => r.trace_id ?? '',
          mono: true,
        },
      ]}
    />
  );
}
